<template>
  <div class="flex flex-column gap-3">
    <applied-rules :visible.sync="visibleAppliedRules" />

    <auth-filters :visible.sync="visibleFiltersModal" :currentFilters.sync="filters" />

    <auth-modal
      :visible.sync="visibleAuthModal"
      :shiftId.sync="authorizationShift"
      :shifts-list="tableData.map(item => item.shift.id)"
      :get-filtered="
        authorizations =>
          filters?.auth_types?.length
            ? authorizations.filter(item => filters.auth_types.includes(item.type))
            : authorizations
      "
      @update="afterAuthorizationProcess"
    />

    <table-layout>
      <template #header><span></span> </template>

      <template #controls-left>
        <a-button type="primary" @click="visibleAppliedRules = true" title="Applied Rules" icon="eye">
          View Applied Rules
        </a-button>
      </template>

      <template #controls-right>
        <a-badge :count="countFilters">
          <a-button icon="filter" @click="visibleFiltersModal = true"> Filters </a-button>
        </a-badge>
        <a-button class="flex align-items-center justify-content-center" @click="fetchAuths" title="Refresh Page">
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </template>

      <template #filters>
        <active-table-filters
          :items="activeFilterTags"
          @edit="openFilterEditor"
          @remove="removeFilter"
          @reset="resetFilters"
        />
      </template>

      <a-table
        size="small"
        :rowKey="record => record.shift.id"
        :columns="extendedColumns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1200, y: 450 }"
        @change="handleTableChange"
        :customRow="
          record => ({
            on: {
              click: () => showShiftAuthorizations(record)
            }
          })
        "
      >
        <span slot="authorizations" slot-scope="text, record" class="flex flex-column gap-1">
          <div
            v-for="authGroup in getUniqueAuthTypes(record.authorizations)"
            :key="authGroup.auth.id"
            class="flex gap-1 align-items-center"
          >
            <a-tooltip class="w-fit">
              <template slot="title">
                {{ AUTH_RULES[authGroup.type] ?? 'Description is not available' }}
              </template>
              <a-tag class="m-0" :color="getAuthStatusTagColor(authGroup.auth)">
                {{ convertType(authGroup.type, authGroup.auth.status) }}
                {{ authGroup.count > 1 ? `(${authGroup.count})` : '' }}
              </a-tag>
            </a-tooltip>

            <template v-if="authGroup.auth.confirmation">
              <a-tooltip class="w-fit">
                <template slot="title">
                  {{ getConfirmationStatusText(authGroup.auth) }}
                </template>
                <a-icon
                  width="16"
                  height="16"
                  :style="{
                    color: authGroup.auth.confirmation.status === 'accept' ? '#10b981' : '#ef4444',
                    minWidth: '16px'
                  }"
                  :icon="
                    authGroup.auth.confirmation.status === 'accept' ? 'carbon:checkmark-filled' : 'carbon:close-filled'
                  "
                /> </a-tooltip
            ></template>

            <template v-if="authGroup.auth.details">
              <a-tooltip class="w-fit">
                <template slot="title">Explanation: <span v-html="safeAuthDetails(authGroup.auth.details)" /></template>

                <a-icon width="16" height="16" icon="carbon:information" /> </a-tooltip
            ></template>

            <template v-if="authGroup.auth.resolved_by_associate || authGroup.auth.resolved_by_disciplinary">
              <a-divider type="vertical" />

              <template v-if="authGroup.auth.resolved_by_associate">Resolved</template>
              <template v-else>{{
                authGroup.auth.disciplinary_objects.map(item => AUTH_DISCIPLINARY_ACTIONS[item.model_name]).join(', ')
              }}</template>
            </template>

            <a-divider type="vertical" />

            <history-tags :associate="record.associate" :authorization="authGroup.auth" />
          </div>
        </span>
        <span slot="date" slot-scope="text, record">
          {{
            record.shift.signed_start || record.shift.scheduled_start
              ? formatDateString(record.shift.signed_start ?? record.shift.scheduled_start)
              : '-'
          }}
        </span>
        <span slot="actions" slot-scope="text, record" class="flex gap-2">
          <button-more :record="record" :menu-items="menuItems" />
        </span>
      </a-table>
    </table-layout>
  </div>
</template>

<script>
import AuthHistoryTags from '@/modules/authorizations/components/shift-auth-history.vue'
import { Badge, Button, Divider, notification, Table, Tag, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftsAuthTableMixin from '@/modules/authorizations/mixins/ShiftsAuthTableMixin.js'
import moment from 'moment-timezone'
import ProfileViewAuthorizationsFilters from './profile-view-authorizations-filters.vue'
import ShiftAuthorizationsModal from '@/modules/authorizations/components/modal/shift-authorizations-modal.vue'
import AuthReasonMixin from '@/modules/authorizations/mixins/AuthReasonMixin.js'
import shiftAuths from '@/helpers/shift-auths'
import permissionMixin from '@/mixins/permissionMixin'
import featureMixin from '@/mixins/featureMixin'
import ProfileViewAppliedRules from './profile-view-applied-rules.vue'
import { sanitizeHtml } from '@/utils/safe-html'

const { mapActions: shiftActions, mapGetters: shiftGetters } = createNamespacedHelpers('shifts')
const { mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-button': Button,
    'a-tooltip': Tooltip,
    'a-table': Table,
    'a-tag': Tag,
    'a-badge': Badge,
    'a-divider': Divider,
    'auth-modal': ShiftAuthorizationsModal,
    'auth-filters': ProfileViewAuthorizationsFilters,
    'history-tags': AuthHistoryTags,
    'applied-rules': ProfileViewAppliedRules
  },
  data() {
    return {
      visibleFiltersModal: false,
      visibleAuthModal: false,
      visibleAppliedRules: false,

      filters: {},
      tableData: [],
      authorizationShift: undefined,

      loading: false
    }
  },
  mixins: [ShiftsAuthTableMixin, AuthReasonMixin, permissionMixin, featureMixin],
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...shiftGetters(['getAuthType']),

    extendedColumns() {
      return this.columns.filter(column => !['associate', 'history'].includes(column.key))
    },
    menuItems() {
      return [
        {
          key: 'show-authorizations',
          icon: 'fluent:open-20-regular',
          label: 'Show Violations',
          visible: true,
          handler: record => {
            this.showShiftAuthorizations(record)
          }
        },
        {
          key: 'open-shift',
          icon: 'fluent:open-20-regular',
          label: 'Open Shift',
          visible: this.hasPermission('manage_shifts') && this.hasFeature('view_shifts'),
          handler: record => {
            this.openModal({
              type: 'shiftView',
              data: {
                visible: true,
                edit: () => {},
                shift_id: Number(record.shift.id),
                open_tab: 'info'
              }
            })
          }
        }
      ]
    }
  },
  watch: {
    filters: {
      handler(newVal, prevVal) {
        if (JSON.stringify(newVal) != JSON.stringify(prevVal)) {
          this.pagination.current = 1
          this.fetchAuths()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...shiftActions(['getAuthsByShifts']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    convertType: shiftAuths.convertType,

    safeAuthDetails(details) {
      return sanitizeHtml(details)
    },

    afterAuthorizationProcess(info) {
      if (!info) {
        this.fetchAuths()
        return
      }

      const { authorizations, shift_id } = info

      const authMap = new Map(authorizations.map(auth => [auth.id, auth]))

      this.tableData = this.tableData.map(item => {
        if (item.shift.id !== shift_id) return item

        const updatedAuthorizations = item.authorizations.map(auth =>
          authMap.has(auth.id) ? authMap.get(auth.id) : auth
        )

        return { ...item, authorizations: updatedAuthorizations }
      })
    },

    getUniqueAuthTypes(authorizations) {
      const map = new Map()

      authorizations.forEach(auth => {
        if (!map.has(auth.type)) {
          map.set(auth.type, {
            type: auth.type,
            auth: auth,
            count: 1
          })
        } else {
          map.get(auth.type).count += 1
        }
      })

      return Array.from(map.values())
    },

    async showShiftAuthorizations(record) {
      this.authorizationShift = record.shift.id
      this.visibleAuthModal = true
    },

    formatDateString(dateString) {
      return moment(dateString).format('MM/DD/YYYY')
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.fetchAuths()
    },

    getAuthStatusTagColor(auth) {
      if (auth.status === 'accepted' || (auth.status === 'declined' && auth.delete_date)) {
        return '#10b981'
      } else if (auth.status === 'declined') {
        return '#ef4444'
      }

      return ''
    },

    getConfirmationStatusText(auth) {
      switch (auth.confirmation.status) {
        case 'accept':
          return `${auth.confirmation.author.first_name} ${auth.confirmation.author.last_name} confirmed that they allowed a violation of the company policy.`
        case 'decline':
          return `${auth.confirmation.author.first_name} ${auth.confirmation.author.last_name} didn't confirm that they didn't allow a violation of the company policy.`
        default:
          return ''
      }
    },

    async fetchAuths() {
      const payload = {
        page: this.pagination.current,
        size: this.pagination.pageSize,
        filter: { ...this.filters, associates: [this.associate.id] }
      }

      this.loading = true
      let skipFinally = false

      try {
        const data = await this.getAuthsByShifts(payload)

        this.tableData = data.items
        this.pagination.total = data.total_count
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loading = true
          return
        }
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (!skipFinally) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 700px;
  overflow-y: auto;
}
</style>
